import { useLayoutEffect, useMemo, useState } from 'react';

import { isValid, parse } from 'date-fns';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import ChuteDropdown from '@/components/common/chute-dropdown';
import DateQueryPicker from '@/components/common/date-picker';
import FarbroLocationDropdown from '@/components/common/farbro-location-dropdown';
import { SitesDropdown } from '@/components/common/sites-drop-down';
import TechniciansDropdown from '@/components/common/technicians-dropdown';
import TimeZoneDropdown from '@/components/common/timezone-dropdown';
import useLocalStorage from '@/components/hooks/useLocalStorage';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FARBRO_LOCATIONS } from '@/pages/opu-search/utils/constants';
import { getValidSites } from '@/services/auth';
import { TechnicianItem } from '@/services/lookups/types';
import { Equipment, Site } from '@/types.gql';
import { formatDateWithPattern } from '@/utils';

import { useAspirationContext } from '../hooks/useAspirationContext';
import { FARBRO_SITE_ID, OVARY_SIDE_OPTIONS } from '../utils/constants';

type Props = { className?: string };

const toUrlDate = (date: Date) => formatDateWithPattern(date, 'MM-dd-yyyy');
const fromUrlDate = (value: string) => parse(value, 'MM-dd-yyyy', new Date());

export const AspirationRequirements = ({ className = '' }: Props) => {
  const [params, setParams] = useSearchParams();
  const defaultDateEnabled = useFeatureFlag('Aspiration_Default_Date');

  useLayoutEffect(() => {
    if (!defaultDateEnabled) return;

    if (!params.get('d')) {
      const next = new URLSearchParams(params);
      next.set('d', toUrlDate(new Date()));
      setParams(next, { replace: true });
    }
  }, [defaultDateEnabled, params, setParams]);

  const opuDate = params.get('d');
  const opuSiteId = params.get('sId');
  const location = params.get('l');
  const timeZone = params.get('tz') || '';
  const chuteNumber = params.get('chuteNumber') || '1';
  const me = useAppSelector((state) => state.authentication.me);

  const [date, setDate] = useState<Date>(() => {
    const parsedDate = opuDate ? fromUrlDate(opuDate) : new Date();
    return isValid(parsedDate) ? parsedDate : new Date();
  });

  const handleDateChange = (date: Date | null) => {
    const nextDate = date ?? new Date();
    const next = new URLSearchParams(params);

    setDate(nextDate);
    next.set('d', toUrlDate(nextDate));
    setParams(next);
  };

  const { quickSearchValue, setQuickSearchValue, setAspSite, setOpuTech } =
    useAspirationContext();

  const [ovarySideFirst, setOvarySideFirst] = useLocalStorage(
    'ovarySideFirst',
    OVARY_SIDE_OPTIONS[0],
  );

  const validSites = useMemo(
    () => getValidSites(me),
    [me?.sites, me?.permissions],
  );

  return (
    <section className={`flex w-full max-w-1/2 flex-wrap gap-2 ${className}`}>
      <div>
        {defaultDateEnabled ? (
          <DatePickerInput
            initialDate={date}
            onChange={handleDateChange}
            placeholder="Date"
            className="w-full"
          />
        ) : (
          <DateQueryPicker paramKey="d" className="w-full" />
        )}
      </div>

      <div>
        <SitesDropdown
          field="siteName"
          paramKey="sId"
          className="w-full"
          onChange={(site) => setAspSite(site as Site)}
          validSites={validSites}
        />
      </div>

      <div>
        <TechniciansDropdown
          paramKey="otId"
          placeholder="OPU Tech"
          className="w-full"
          disabled={!opuDate || !opuSiteId}
          onChange={(tech: TechnicianItem | null) =>
            setOpuTech(tech as unknown as Equipment)
          }
        />
      </div>

      {/* 4 ▸ Time zone (writes/reads ?tz=) */}
      <div>
        <TimeZoneDropdown
          paramKey="tz"
          value={timeZone || null}
          className="w-full"
        />
      </div>

      {opuSiteId === FARBRO_SITE_ID && (
        <div>
          <FarbroLocationDropdown
            options={FARBRO_LOCATIONS}
            value={location ?? null}
            disabled={!opuDate}
            className="w-full"
          />
        </div>
      )}

      <div>
        <InputText
          value={quickSearchValue}
          onChange={(e) => setQuickSearchValue(e.target.value)}
          placeholder="Search"
          className="w-full"
        />
      </div>
      {opuSiteId !== FARBRO_SITE_ID && (
        <div>
          <ChuteDropdown
            value={chuteNumber !== '1' ? chuteNumber : null}
            className="w-full"
          />
        </div>
      )}
      <div className="flex min-w-[12rem] flex-1 items-center">
        <SelectButton
          value={ovarySideFirst}
          onChange={(e) => setOvarySideFirst(e.value)}
          options={OVARY_SIDE_OPTIONS}
          className="w-full"
        />
      </div>
    </section>
  );
};
